// src/pages/SupervisorMonitor.jsx
// ✅ ADMIN FEATURE: Real-time supervisor monitoring & GPS tracking
// Solves: "We don't know if supervisors are really working or just getting paid for nothing"

import React, { useState, useEffect, useRef } from 'react';
import { 
  collection, query, where, getDocs, onSnapshot,
  orderBy, limit, Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { ROLES, isAdminOrHigher } from '../services/authService';
import './SupervisorMonitor.css';

function SupervisorMonitor({ user, onBack }) {
  const [supervisors, setSupervisors] = useState([]);
  const [clockRecords, setClockRecords] = useState([]);
  const [locationHistory, setLocationHistory] = useState([]);
  const [serviceRecords, setServiceRecords] = useState([]);
  const [liveLocations, setLiveLocations] = useState({}); // ✅ NEW: supervisorId -> latest live location doc
  const [loading, setLoading] = useState(true);
  const [selectedSupervisor, setSelectedSupervisor] = useState(null);
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'location' | 'activity'
  const [timeFilter, setTimeFilter] = useState('today'); // 'today' | 'week' | 'month'
  const liveUnsubsRef = useRef([]);

  // ✅ ACCESS CONTROL: Only admin or higher
  useEffect(() => {
    if (!isAdminOrHigher(user)) {
      alert('Access Denied: Only Admin can monitor supervisors.');
      onBack();
      return;
    }
    loadData();
  }, [user]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load all supervisors
      const supervisorsSnap = await getDocs(
        query(
          collection(db, 'users'),
          where('role', '==', ROLES.SUPERVISOR),
          where('active', '==', true)
        )
      );
      const sups = supervisorsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      setSupervisors(sups);

      // Load recent clock events (last 30 days)
      // ✅ FIXED: this app's ClockInOut.jsx writes individual clock-in/clock-out
      // EVENTS (type: 'in' | 'out') to 'clockEvents', with a plain numeric
      // 'timestamp' (Date.now()) — not a combined clockInTime/clockOutTime record.
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const clockSnap = await getDocs(
        query(
          collection(db, 'clockEvents'),
          where('timestamp', '>=', thirtyDaysAgo.getTime()),
          orderBy('timestamp', 'desc')
        )
      );
      setClockRecords(clockSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load location history (last 30 days)
      const locationSnap = await getDocs(
        query(
          collection(db, 'locationLogs'),
          where('timestamp', '>=', Timestamp.fromDate(thirtyDaysAgo)),
          orderBy('timestamp', 'desc')
        )
      );
      setLocationHistory(locationSnap.docs.map(d => ({ id: d.id, ...d.data() })));

      // Load service records (to verify field visits)
      const serviceSnap = await getDocs(
        query(
          collection(db, 'serviceRecords'),
          where('createdAt', '>=', Timestamp.fromDate(thirtyDaysAgo)),
          orderBy('createdAt', 'desc')
        )
      );
      setServiceRecords(serviceSnap.docs.map(d => ({ id: d.id, ...d.data() })));

    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  // ✅ NEW: Auto-track — live GPS pin per supervisor, always on (not just when one is selected).
  // Reuses the same (userId ==, timestamp desc) composite index the detail view already relies on,
  // so no additional Firestore index should be required. If Firestore ever throws a
  // "query requires an index" error, click the link in that error to auto-create it, or add manually:
  // Firestore Console → locationHistory → Composite index: userId (Asc), timestamp (Desc).
  useEffect(() => {
    // Clean up any previous listeners before re-subscribing
    liveUnsubsRef.current.forEach(unsub => unsub());
    liveUnsubsRef.current = [];

    if (supervisors.length === 0) return;

    supervisors.forEach(sup => {
      const unsub = onSnapshot(
        query(
          collection(db, 'locationLogs'),
          where('userId', '==', sup.id),
          orderBy('timestamp', 'desc'),
          limit(1)
        ),
        (snap) => {
          if (snap.empty) return;
          const docData = { id: snap.docs[0].id, ...snap.docs[0].data() };
          setLiveLocations(prev => ({ ...prev, [sup.id]: docData }));
        },
        (err) => console.error('Live location listener error:', err)
      );
      liveUnsubsRef.current.push(unsub);
    });

    return () => {
      liveUnsubsRef.current.forEach(unsub => unsub());
      liveUnsubsRef.current = [];
    };
  }, [supervisors]);

  // Real-time location updates for selected supervisor
  useEffect(() => {
    if (!selectedSupervisor) return;

    const unsubscribe = onSnapshot(
      query(
        collection(db, 'locationLogs'),
        where('userId', '==', selectedSupervisor.id),
        orderBy('timestamp', 'desc'),
        limit(50)
      ),
      (snapshot) => {
        const locations = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setLocationHistory(prev => {
          // Merge with existing, remove duplicates
          const merged = [...locations, ...prev.filter(l => l.userId !== selectedSupervisor.id)];
          return merged;
        });
      }
    );

    return () => unsubscribe();
  }, [selectedSupervisor]);

  // ✅ FIXED: 'clockEvents' stores individual in/out events (type: 'in' | 'out'),
  // not one combined record with clockInTime/clockOutTime. This reconstructs
  // "sessions" (a clock-in paired with its clock-out, or an open session if
  // still clocked in) the same way ClockInOut.jsx itself does.
  const getSupervisorSessions = (supervisorId) => {
    const events = clockRecords
      .filter(c => c.userId === supervisorId)
      .slice()
      .sort((a, b) => a.timestamp - b.timestamp); // ascending

    const sessions = [];
    let pendingIn = null;

    events.forEach(ev => {
      if (ev.type === 'in') {
        pendingIn = ev;
      } else if (ev.type === 'out' && pendingIn) {
        const clockInTime = new Date(pendingIn.timestamp);
        const clockOutTime = new Date(ev.timestamp);
        const durationHours = typeof ev.sessionDuration === 'number'
          ? ev.sessionDuration / 3600
          : (clockOutTime - clockInTime) / (1000 * 60 * 60);
        sessions.push({ clockInTime, clockOutTime, durationHours, open: false });
        pendingIn = null;
      }
    });

    // Still clocked in — open session running to "now"
    if (pendingIn) {
      const clockInTime = new Date(pendingIn.timestamp);
      sessions.push({
        clockInTime,
        clockOutTime: null,
        durationHours: (Date.now() - pendingIn.timestamp) / (1000 * 60 * 60),
        open: true
      });
    }

    return sessions.sort((a, b) => b.clockInTime - a.clockInTime); // most recent first
  };

  // Get supervisor stats
  const getSupervisorStats = (supervisor) => {
    const now = new Date();
    let startDate = new Date();
    
    if (timeFilter === 'today') {
      startDate.setHours(0, 0, 0, 0);
    } else if (timeFilter === 'week') {
      startDate.setDate(startDate.getDate() - 7);
    } else {
      startDate.setDate(startDate.getDate() - 30);
    }

    // Sessions for this supervisor, filtered to the active time window
    const allSessions = getSupervisorSessions(supervisor.id);
    const recentSessions = allSessions.filter(s => s.clockInTime >= startDate);

    // Location history
    const supLocations = locationHistory.filter(l => l.userId === supervisor.id);
    const recentLocations = supLocations.filter(l => {
      const locDate = l.timestamp?.toDate ? l.timestamp.toDate() : new Date(l.timestamp);
      return locDate >= startDate;
    });

    // Service records (as supervisor or technician)
    const supServices = serviceRecords.filter(s => 
      s.technicianId === supervisor.id || s.supervisorId === supervisor.id
    );
    const recentServices = supServices.filter(s => {
      const serviceDate = s.createdAt?.toDate ? s.createdAt.toDate() : new Date(s.createdAt);
      return serviceDate >= startDate;
    });

    // Current status — an "open" session means they're still clocked in
    const openSession = allSessions.find(s => s.open);
    const isCurrentlyLoggedIn = !!openSession;
    const onlineSince = openSession ? openSession.clockInTime : null;

    // ✅ NEW: Prefer the live-tracked pin (auto-updating) over the static 30-day snapshot,
    // falling back gracefully if the live listener hasn't returned data yet.
    const live = liveLocations[supervisor.id];
    const lastLocation = live || recentLocations[0];
    const lastLocationTime = lastLocation
      ? (lastLocation.timestamp?.toDate ? lastLocation.timestamp.toDate() : new Date(lastLocation.timestamp))
      : null;

    // Total working hours across sessions in the active window
    const totalHours = recentSessions.reduce((sum, s) => sum + s.durationHours, 0);
    const avgHoursPerDay = recentSessions.length > 0 ? (totalHours / recentSessions.length) : 0;

    return {
      clockedInDays: recentSessions.length,
      totalHours: totalHours.toFixed(1),
      avgHoursPerDay: avgHoursPerDay.toFixed(1), // ✅ average work time per logged day
      serviceRecords: recentServices.length,
      locationUpdates: recentLocations.length,
      isOnline: isCurrentlyLoggedIn,
      onlineSince,
      lastLocation,
      lastLocationTime,
      isLiveLocation: !!live, // true if this pin came from the auto-tracking listener
      productivity: recentSessions.length > 0 ? (recentServices.length / recentSessions.length).toFixed(2) : '0'
    };
  };

  // Verify if supervisor is where they claim to be
  const verifyLocation = (supervisor) => {
    const stats = getSupervisorStats(supervisor);
    if (!stats.lastLocation) return { status: 'unknown', message: 'No location data' };

    const now = new Date();
    const timeSinceUpdate = stats.lastLocationTime ? 
      Math.floor((now - stats.lastLocationTime) / (1000 * 60)) : null;

    if (!timeSinceUpdate) return { status: 'unknown', message: 'No recent location' };

    if (timeSinceUpdate > 60) {
      return { 
        status: 'warning', 
        message: `Last seen ${timeSinceUpdate} minutes ago` 
      };
    }

    if (stats.isOnline && timeSinceUpdate > 30) {
      return {
        status: 'warning',
        message: `Logged in but no GPS update for ${timeSinceUpdate} minutes`
      };
    }

    return {
      status: 'verified',
      message: `Location updated ${timeSinceUpdate} minute${timeSinceUpdate !== 1 ? 's' : ''} ago`
    };
  };

  // Intelligence: Flag suspicious behavior
  const getIntelligenceFlags = (supervisor) => {
    const stats = getSupervisorStats(supervisor);
    const flags = [];

    // Flag 1: Logged in but zero service records
    if (stats.clockedInDays >= 2 && stats.serviceRecords === 0) {
      flags.push({
        severity: 'critical',
        title: 'Zero Output',
        detail: `Logged in ${stats.clockedInDays} times but recorded NO services`
      });
    }

    // Flag 2: Low productivity
    if (stats.clockedInDays >= 5 && parseFloat(stats.productivity) < 0.5) {
      flags.push({
        severity: 'high',
        title: 'Low Productivity',
        detail: `Only ${stats.productivity} services per login (expected: 1.0+)`
      });
    }

    // Flag 3: GPS not updating while logged in
    const verification = verifyLocation(supervisor);
    if (stats.isOnline && verification.status === 'warning') {
      flags.push({
        severity: 'high',
        title: 'GPS Issue',
        detail: verification.message
      });
    }

    // Flag 4: Very few location updates vs clock-ins
    if (stats.clockedInDays >= 5 && stats.locationUpdates < stats.clockedInDays * 3) {
      flags.push({
        severity: 'medium',
        title: 'Insufficient GPS Tracking',
        detail: `Only ${stats.locationUpdates} GPS updates for ${stats.clockedInDays} work days`
      });
    }

    return flags;
  };

  // ✅ NEW: Export all supervisors' current stats to a CSV report
  const exportOverviewReport = () => {
    const rows = [
      ['Name', 'Phone', 'Region', 'Status', 'Online Since', 'Days Logged', 'Total Hours', 'Avg Hrs/Day', 'Services', 'Productivity', 'Last Location', 'Last GPS Update', 'GPS Verification']
    ];

    supervisors.forEach(sup => {
      const stats = getSupervisorStats(sup);
      const verification = verifyLocation(sup);
      rows.push([
        sup.name || '',
        sup.phone || '',
        sup.region || 'All Regions',
        stats.isOnline ? 'Online' : 'Offline',
        stats.onlineSince ? stats.onlineSince.toLocaleTimeString() : '',
        stats.clockedInDays,
        stats.totalHours,
        stats.avgHoursPerDay,
        stats.serviceRecords,
        stats.productivity,
        stats.lastLocation ? `${stats.lastLocation.latitude?.toFixed(6)}, ${stats.lastLocation.longitude?.toFixed(6)}` : '',
        stats.lastLocationTime ? stats.lastLocationTime.toLocaleString() : '',
        verification.message
      ]);
    });

    downloadCsv(rows, `supervisor-monitor-${timeFilter}-${new Date().toISOString().slice(0, 10)}.csv`);
  };

  // ✅ NEW: Export a single supervisor's clock-in/out log (days logged)
  // Takes sessions built by getSupervisorSessions() — clockInTime/clockOutTime
  // are already plain JS Dates, durationHours already computed.
  const exportSupervisorClockLog = (supervisor, sessions) => {
    const rows = [['Date', 'Clock In', 'Clock Out', 'Duration (hrs)']];
    sessions.forEach(session => {
      rows.push([
        session.clockInTime.toLocaleDateString(),
        session.clockInTime.toLocaleTimeString(),
        session.clockOutTime ? session.clockOutTime.toLocaleTimeString() : '',
        session.open ? 'In Progress' : session.durationHours.toFixed(1)
      ]);
    });
    downloadCsv(rows, `${(supervisor.name || 'supervisor').replace(/\s+/g, '-').toLowerCase()}-days-logged.csv`);
  };

  const downloadCsv = (rows, filename) => {
    const csvContent = rows
      .map(row => row.map(cell => `"${String(cell).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="svm-page">
        <div className="svm-loading-wrap">
          <div className="svm-spinner"></div>
          <p className="svm-loading-text">Loading Supervisor Data...</p>
        </div>
      </div>
    );
  }

  if (!isAdminOrHigher(user)) {
    return (
      <div className="svm-page">
        <div className="svm-denied">
          <div className="svm-denied-icon">🔒</div>
          <h2>Access Denied</h2>
          <p>
            Only Admin can monitor supervisors.
          </p>
        </div>
      </div>
    );
  }

  // Detailed view for selected supervisor
  if (selectedSupervisor) {
    const stats = getSupervisorStats(selectedSupervisor);
    const verification = verifyLocation(selectedSupervisor);
    const flags = getIntelligenceFlags(selectedSupervisor);

    // Get supervisor's location history
    const supLocations = locationHistory.filter(l => l.userId === selectedSupervisor.id);
    const supClocks = getSupervisorSessions(selectedSupervisor.id);
    const supServices = serviceRecords.filter(s => s.technicianId === selectedSupervisor.id);

    return (
      <div className="svm-page">
        {/* Header */}
        <div className="svm-header">
          <button className="back-button" onClick={() => setSelectedSupervisor(null)}>
            ← Back
          </button>
          <div className="svm-header-center">
            <h2 className="svm-header-title">{selectedSupervisor.name}</h2>
            <span className="svm-header-sub">
              {selectedSupervisor.phone} • {selectedSupervisor.region || 'All Regions'}
            </span>
          </div>
          <button className="svm-refresh-btn" onClick={loadData}>🔄</button>
        </div>

        {/* Status Banner */}
        <div className={`svm-status-banner ${stats.isOnline ? 'online' : 'offline'}`}>
          <div className="svm-status-icon">
            {stats.isOnline ? '🟢' : '⚪'}
          </div>
          <div>
            <div className="svm-status-title">
              {stats.isOnline ? 'Currently Logged In' : 'Offline'}
              {/* ✅ NEW: show the time they logged in, while online */}
              {stats.isOnline && stats.onlineSince && (
                <span className="svm-status-since"> · since {stats.onlineSince.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
              )}
            </div>
            <div className="svm-status-sub">
              {verification.message}
              {/* ✅ NEW: badge showing the pin is auto-tracking live, not a stale snapshot */}
              {stats.isLiveLocation && <span className="svm-live-badge">● LIVE</span>}
            </div>
          </div>
        </div>

        {/* Intelligence Flags */}
        {flags.length > 0 && (
          <div className="svm-flags-wrap">
            {flags.map((flag, i) => (
              <div key={i} className={`svm-flag-alert sev-${flag.severity}`}>
                <div className="svm-flag-icon">
                  {flag.severity === 'critical' ? '🔴' : flag.severity === 'high' ? '🟠' : '🟡'}
                </div>
                <div>
                  <div className="svm-flag-title">
                    {flag.title}
                  </div>
                  <div className="svm-flag-detail">
                    {flag.detail}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Stats Cards */}
        <div className="svm-stats-grid svm-stats-grid-5">
          <div className="svm-stat-card">
            <div className="svm-stat-icon">📅</div>
            <div className="svm-stat-value">{stats.clockedInDays}</div>
            <div className="svm-stat-label">Days Logged</div>
          </div>
          <div className="svm-stat-card">
            <div className="svm-stat-icon">⏱️</div>
            <div className="svm-stat-value">{stats.totalHours}h</div>
            <div className="svm-stat-label">Total Hours</div>
          </div>
          {/* ✅ NEW: average work time */}
          <div className="svm-stat-card">
            <div className="svm-stat-icon">📈</div>
            <div className="svm-stat-value">{stats.avgHoursPerDay}h</div>
            <div className="svm-stat-label">Avg/Day</div>
          </div>
          <div className="svm-stat-card">
            <div className="svm-stat-icon">📝</div>
            <div className="svm-stat-value">{stats.serviceRecords}</div>
            <div className="svm-stat-label">Services</div>
          </div>
          <div className="svm-stat-card">
            <div className="svm-stat-icon">📍</div>
            <div className="svm-stat-value">{stats.locationUpdates}</div>
            <div className="svm-stat-label">GPS Updates</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="svm-tabs">
          {['overview', 'location', 'activity'].map(tab => (
            <button
              key={tab}
              className={`svm-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {tab === 'overview' ? '📊 Overview' : tab === 'location' ? '📍 Location' : '📅 Activity'}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="svm-tab-content">
          {activeTab === 'overview' && (
            <div>
              <div className="svm-section">
                <h3 className="svm-section-title">Performance Summary</h3>
                <div className="svm-metric">
                  <span>Productivity Rate:</span>
                  <span className={`svm-metric-value ${parseFloat(stats.productivity) >= 1 ? 'good' : 'warn'}`}>
                    {stats.productivity} services/login
                  </span>
                </div>
                <div className="svm-metric">
                  <span>Average Work Time:</span>
                  <span className="svm-metric-value">
                    {stats.avgHoursPerDay} hrs/day
                  </span>
                </div>
                <div className="svm-metric">
                  <span>GPS Tracking:</span>
                  <span className={`svm-metric-value ${stats.locationUpdates >= stats.clockedInDays * 3 ? 'good' : 'warn'}`}>
                    {stats.locationUpdates >= stats.clockedInDays * 3 ? 'Good' : 'Needs Improvement'}
                  </span>
                </div>
              </div>

              <div className="svm-section">
                <div className="svm-section-header-row">
                  <h3 className="svm-section-title">Days Logged</h3>
                  {/* ✅ NEW: export this supervisor's clock log */}
                  <button className="svm-export-btn-inline" onClick={() => exportSupervisorClockLog(selectedSupervisor, supClocks)}>
                    ⬇️ Export
                  </button>
                </div>
                {supClocks.slice(0, 5).length > 0 ? (
                  supClocks.slice(0, 5).map((session, i) => (
                    <div key={i} className="svm-list-item">
                      <div>
                        <div className="svm-list-title">{session.clockInTime.toLocaleDateString()}</div>
                        <div className="svm-list-sub">
                          In: {session.clockInTime.toLocaleTimeString()} {session.clockOutTime ? `• Out: ${session.clockOutTime.toLocaleTimeString()}` : ''}
                        </div>
                      </div>
                      <div className={`svm-duration ${session.open ? 'pending' : 'done'}`}>
                        {session.open ? 'In Progress' : `${session.durationHours.toFixed(1)}h`}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="svm-empty-inline">No days logged yet</div>
                )}
              </div>

              <div className="svm-section">
                <h3 className="svm-section-title">Recent Services</h3>
                {supServices.slice(0, 5).length > 0 ? (
                  supServices.slice(0, 5).map(service => {
                    const serviceDate = service.createdAt?.toDate ? service.createdAt.toDate() : new Date(service.createdAt);
                    return (
                      <div key={service.id} className="svm-list-item">
                        <div>
                          <div className="svm-list-title">
                            {service.outletName}
                          </div>
                          <div className="svm-list-sub">
                            {serviceDate.toLocaleDateString()} • {service.serviceType}
                          </div>
                        </div>
                        <div className="svm-list-status">
                          {service.status || 'Completed'}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="svm-empty-inline">
                    No services recorded
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'location' && (
            <div>
              {stats.lastLocation ? (
                <>
                  <div className="svm-section">
                    <h3 className="svm-section-title">
                      Current Location
                      {stats.isLiveLocation && <span className="svm-live-badge">● LIVE</span>}
                    </h3>
                    <div className="svm-location-card">
                      <div className="svm-location-pin">📍</div>
                      <div className="svm-location-coords">
                        {stats.lastLocation.latitude?.toFixed(6)}, {stats.lastLocation.longitude?.toFixed(6)}
                      </div>
                      <div className="svm-location-meta">
                        Accuracy: ±{stats.lastLocation.accuracy?.toFixed(0)}m
                      </div>
                      <div className="svm-location-meta svm-location-meta-updated">
                        Updated: {stats.lastLocationTime?.toLocaleString()}
                      </div>
                      <a
                        href={`https://www.google.com/maps?q=${stats.lastLocation.latitude},${stats.lastLocation.longitude}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="svm-map-btn"
                      >
                        🗺️ View on Google Maps
                      </a>
                    </div>
                  </div>

                  <div className="svm-section">
                    <h3 className="svm-section-title">Location History (Last 10)</h3>
                    {supLocations.slice(0, 10).map(loc => {
                      const locTime = loc.timestamp?.toDate ? loc.timestamp.toDate() : new Date(loc.timestamp);
                      return (
                        <div key={loc.id} className="svm-list-item">
                          <div>
                            <div className="svm-list-title">
                              {loc.latitude?.toFixed(6)}, {loc.longitude?.toFixed(6)}
                            </div>
                            <div className="svm-list-sub">
                              {locTime.toLocaleString()} • {loc.context || 'Background'}
                            </div>
                          </div>
                          <a
                            href={`https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="svm-map-link"
                          >
                            Map →
                          </a>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="svm-empty-block">
                  <div className="svm-empty-block-icon">📍</div>
                  <p>No location data available</p>
                </div>
              )}
            </div>
          )}

          {activeTab === 'activity' && (
            <div>
              <div className="svm-section">
                <div className="svm-section-header-row">
                  <h3 className="svm-section-title">Clock Records</h3>
                  <button className="svm-export-btn-inline" onClick={() => exportSupervisorClockLog(selectedSupervisor, supClocks)}>
                    ⬇️ Export
                  </button>
                </div>
                {supClocks.slice(0, 10).length > 0 ? (
                  supClocks.slice(0, 10).map((session, i) => (
                    <div key={i} className="svm-list-item">
                      <div>
                        <div className="svm-list-title">
                          {session.clockInTime.toLocaleDateString()}
                        </div>
                        <div className="svm-list-sub">
                          In: {session.clockInTime.toLocaleTimeString()} {session.clockOutTime ? `• Out: ${session.clockOutTime.toLocaleTimeString()}` : ''}
                        </div>
                      </div>
                      <div className={`svm-duration ${session.open ? 'pending' : 'done'}`}>
                        {session.open ? 'In Progress' : `${session.durationHours.toFixed(1)}h`}
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="svm-empty-inline">
                    No clock records
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Overview: List of all supervisors
  return (
    <div className="svm-page">
      {/* Header */}
      <div className="svm-header">
        <button className="back-button" onClick={onBack}>← Back</button>
        <div className="svm-header-center">
          <h2 className="svm-header-title">Supervisor Monitor</h2>
          <span className="svm-header-sub">
            Real-time tracking & accountability
          </span>
        </div>
        {/* ✅ NEW: export a full CSV report of every supervisor's current stats */}
        <button className="svm-export-btn" onClick={exportOverviewReport} title="Export Report">⬇️ Export</button>
        <button className="svm-refresh-btn" onClick={loadData}>🔄</button>
      </div>

      {/* Time Filter */}
      <div className="svm-filter-row">
        {['today', 'week', 'month'].map(filter => (
          <button
            key={filter}
            className={`svm-filter-pill ${timeFilter === filter ? 'active' : ''}`}
            onClick={() => setTimeFilter(filter)}
          >
            {filter === 'today' ? '📅 Today' : filter === 'week' ? '📅 7 Days' : '📅 30 Days'}
          </button>
        ))}
      </div>

      {/* Supervisor Cards */}
      <div className="svm-supervisor-list">
        {supervisors.length === 0 ? (
          <div className="svm-empty-block">
            <div className="svm-empty-block-icon">🎯</div>
            <p>No active supervisors found</p>
          </div>
        ) : (
          supervisors.map(supervisor => {
            const stats = getSupervisorStats(supervisor);
            const flags = getIntelligenceFlags(supervisor);
            const verification = verifyLocation(supervisor);

            return (
              <div
                key={supervisor.id}
                className="svm-supervisor-card"
                onClick={() => setSelectedSupervisor(supervisor)}
              >
                {/* Status Indicator */}
                <div className={`svm-status-dot ${stats.isOnline ? 'online' : ''}`}></div>

                {/* Flag Count */}
                {flags.length > 0 && (
                  <div className={`svm-flag-count-badge ${flags.some(f => f.severity === 'critical') ? 'critical' : ''}`}>
                    {flags.length} {flags.length === 1 ? 'Flag' : 'Flags'}
                  </div>
                )}

                {/* Supervisor Info */}
                <div className="svm-supervisor-name-block">
                  <div className="svm-supervisor-name">
                    {supervisor.name}
                  </div>
                  <div className="svm-supervisor-meta">
                    {supervisor.phone} • {supervisor.region || 'All Regions'}
                  </div>
                  {/* ✅ NEW: "logged in since" line, only while online */}
                  {stats.isOnline && stats.onlineSince && (
                    <div className="svm-supervisor-online-since">
                      Online since {stats.onlineSince.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  )}
                </div>

                {/* Quick Stats */}
                <div className="svm-mini-stats-grid svm-mini-stats-grid-5">
                  <div className="svm-mini-stat">
                    <div className="svm-mini-stat-value">{stats.clockedInDays}</div>
                    <div className="svm-mini-stat-label">Days</div>
                  </div>
                  <div className="svm-mini-stat">
                    <div className="svm-mini-stat-value">{stats.totalHours}h</div>
                    <div className="svm-mini-stat-label">Hours</div>
                  </div>
                  {/* ✅ NEW: average work time on the list card too */}
                  <div className="svm-mini-stat">
                    <div className="svm-mini-stat-value">{stats.avgHoursPerDay}h</div>
                    <div className="svm-mini-stat-label">Avg/Day</div>
                  </div>
                  <div className="svm-mini-stat">
                    <div className="svm-mini-stat-value">{stats.serviceRecords}</div>
                    <div className="svm-mini-stat-label">Services</div>
                  </div>
                  <div className="svm-mini-stat">
                    <div className="svm-mini-stat-value">{stats.productivity}</div>
                    <div className="svm-mini-stat-label">Prod</div>
                  </div>
                </div>

                {/* Status */}
                <div className={`svm-verify-status ${verification.status === 'verified' ? 'verified' : 'warning'}`}>
                  {verification.status === 'verified' ? '✅' : '⚠️'} {verification.message}
                  {/* ✅ NEW: live pin indicator */}
                  {stats.isLiveLocation && <span className="svm-live-badge">● LIVE</span>}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

export default SupervisorMonitor;
